/**
 * The Threads tab – one row per conversation: counterpart, phase, whose turn,
 * the deadline with its source, the last step, the references. A row opens
 * its history, the participants and the owner's step-in controls:
 * "Christoph only" (the seats stay out), the owner action done, close and
 * reopen. Every one of them is reversible, so each gets an Undo toast.
 * A thread whose participant has an open task (a LinkedIn message, a call)
 * names it under the turn – the reason the thread waits.
 * `?thread=<id>` opens and highlights one thread (needs-you links use it).
 */
import { Fragment, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { clsx } from "clsx";

import { usePatchThread, type ClosedAs, type ConsoleThread, type ThreadPatch } from "../../api/console";
import { CountryLabel } from "../../components/b2g/CountryLabel";
import { DueLine, TurnPill } from "../../components/console/parts";
import { Pill } from "../../components/Pill";
import { useToast } from "../../components/toast/context";
import {
  CLOSED_LABEL,
  CLOSE_OPTIONS,
  berlinDate,
  counterpart,
  currentParticipants,
  participantLabel,
  sortThreads,
  stepArrow,
} from "../../lib/console";
import { taskTypeLabel } from "../../lib/taskGroups";

export function ThreadsTab({
  threads,
  focusId,
  onFocus,
}: {
  threads: ConsoleThread[];
  focusId: string | null;
  onFocus: (id: string | null) => void;
}) {
  const [showClosed, setShowClosed] = useState(false);
  const sorted = sortThreads(threads);
  const closedCount = threads.filter((t) => t.status === "closed").length;
  const visible = sorted.filter((t) => showClosed || t.status === "open" || t.id === focusId);

  if (!threads.length) {
    return (
      <div className="panel text-sm text-ink-low">
        No thread yet. A thread starts with the first logged message to a member of this campaign.
      </div>
    );
  }
  return (
    <div className="panel panel--flush overflow-x-auto">
      <div className="panel-header">
        <span className="text-xs text-ink-low">
          your turn first, then ours, then theirs – each by deadline. The server records the facts from the logged messages.
        </span>
        {closedCount ? (
          <button
            type="button"
            className={clsx("btn btn-sm", showClosed ? "btn-main" : "btn-ghost")}
            onClick={() => setShowClosed(!showClosed)}
          >
            Show closed <span className="tnum">{closedCount}</span>
          </button>
        ) : null}
      </div>
      <table className="table">
        <thead>
          <tr>
            <th>Counterpart</th>
            <th>Phase</th>
            <th>Whose turn</th>
            <th>Due</th>
            <th>Last step</th>
            <th>Refs</th>
          </tr>
        </thead>
        <tbody>
          {visible.map((t) => (
            <ThreadRow
              key={t.id}
              thread={t}
              expanded={focusId === t.id}
              onToggle={() => onFocus(focusId === t.id ? null : t.id)}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ThreadRow({ thread: t, expanded, onToggle }: { thread: ConsoleThread; expanded: boolean; onToggle: () => void }) {
  const rowRef = useRef<HTMLTableRowElement>(null);
  useEffect(() => {
    if (expanded) rowRef.current?.scrollIntoView({ block: "nearest" });
  }, [expanded]);
  const steps = t.steps ?? [];
  const last = steps[steps.length - 1];
  const current = currentParticipants(t)[0];
  return (
    <Fragment>
      <tr ref={rowRef} className={clsx("cursor-pointer align-top", expanded && "row-cursor")} onClick={onToggle}>
        <td>
          <div className="font-heading font-semibold text-ink">{counterpart(t)}</div>
          <div className="text-xs text-ink-low">
            {t.market ? <CountryLabel iso2={t.market} /> : null}
            {t.market && current && t.company_name ? " · " : null}
            {t.company_name && current ? participantLabel(current) : null}
          </div>
          <div className="flex flex-wrap gap-1 mt-1">
            {t.christoph_only ? <Pill value="Christoph only" tone="alert" /> : null}
            {t.status === "closed" ? <Pill value={`closed · ${CLOSED_LABEL[t.closed_as ?? "other"]}`} tone="muted" /> : null}
          </div>
        </td>
        <td className="text-sm">{t.phase}</td>
        <td>
          <TurnPill turn={t.turn} />
          {t.owner_action ? <div className="text-xs text-danger mt-1">{t.owner_action}</div> : null}
          {(t.open_tasks ?? []).map((task) => (
            <div key={task.task_id} className="text-xs text-ink-medium mt-1">
              <Link to={`/tasks?task=${task.task_id}`} className="link-quiet" onClick={(e) => e.stopPropagation()}>
                {taskTypeLabel(task.task_type)} waiting for you
              </Link>
              {task.title ? <span className="text-ink-low"> – {task.title}</span> : null}
            </div>
          ))}
        </td>
        <td className="text-sm">
          <DueLine due={t.due} />
        </td>
        <td className="text-sm max-w-xs">
          {last ? (
            <>
              <span className="font-mono text-xs text-ink-low">
                {berlinDate(last.at)} {last.channel}
              </span>{" "}
              {stepArrow(last.dir)} {last.subject || last.text}
              {last.auto ? <span className="text-xs text-ink-low"> (automatic – not an answer)</span> : null}
            </>
          ) : (
            <span className="text-ink-low">–</span>
          )}
        </td>
        <td className="text-xs font-mono text-ink-medium whitespace-nowrap">
          {t.refs.ours ? <div>ours {t.refs.ours}</div> : null}
          {t.refs.theirs ? <div>theirs {t.refs.theirs}</div> : null}
        </td>
      </tr>
      {expanded ? (
        <tr>
          <td colSpan={6} className="bg-surface-tint">
            <ThreadDetail thread={t} />
          </td>
        </tr>
      ) : null}
    </Fragment>
  );
}

function ThreadDetail({ thread: t }: { thread: ConsoleThread }) {
  const patch = usePatchThread();
  const toast = useToast();
  const [closeAs, setCloseAs] = useState<ClosedAs>("no_reply");
  const [closeNote, setCloseNote] = useState("");
  const steps = t.steps ?? [];

  const apply = (body: ThreadPatch, message: string, undo?: ThreadPatch) =>
    patch.mutate(
      { id: t.id, body },
      {
        onSuccess: () =>
          toast.ok(message, undo ? { action: { label: "Undo", onClick: () => patch.mutate({ id: t.id, body: undo }) } } : undefined),
        onError: (e) => toast.error(e),
      },
    );

  return (
    <div className="grid md:grid-cols-2 gap-5 py-2">
      <div>
        <div className="text-xs text-ink-low mb-1">Steps · oldest first</div>
        {steps.length ? (
          <ul className="text-sm space-y-1">
            {steps.map((s, i) => (
              <li key={i} className={clsx(s.auto && "text-ink-low")}>
                <span className="font-mono text-xs text-ink-low">
                  {berlinDate(s.at)} {s.channel}
                </span>{" "}
                {stepArrow(s.dir)} {s.subject ? <span className="font-semibold">{s.subject}</span> : null}
                {s.subject && s.text ? " – " : null}
                {s.text}
                {s.auto ? <span className="text-xs"> (automatic)</span> : null}
              </li>
            ))}
          </ul>
        ) : (
          <div className="text-sm text-ink-low">No logged message yet.</div>
        )}
        {t.subject ? <div className="text-xs text-ink-low mt-2">Subject: {t.subject}</div> : null}
        {t.mailbox ? <div className="text-xs text-ink-low font-mono">{t.mailbox}</div> : null}
      </div>
      <div className="space-y-3">
        <div>
          <div className="text-xs text-ink-low mb-1">Participants</div>
          <ul className="text-sm space-y-1">
            {t.participants.map((p, i) => (
              <li key={p.contact_id ?? p.email ?? i}>
                {p.contact_id ? (
                  <Link to={`/contacts/${p.contact_id}`} className={clsx("link", p.state === "left" && "line-through text-ink-low")}>
                    {participantLabel(p)}
                  </Link>
                ) : (
                  <span className={clsx(p.state === "left" && "line-through text-ink-low")}>{participantLabel(p)}</span>
                )}
                {p.email && p.name ? <span className="text-xs text-ink-low font-mono"> {p.email}</span> : null}
                {p.note ? <span className="text-xs text-ink-low"> – {p.note}</span> : null}
              </li>
            ))}
          </ul>
        </div>
        {t.responsible_agent_slug ? (
          <div className="text-xs text-ink-low">
            Seat <span className="font-mono">{t.responsible_agent_slug}</span>
          </div>
        ) : null}
        {t.owner_action ? (
          <div className="callout callout--warn text-sm">
            <div className="flex items-center gap-3">
              <span className="flex-1">Your turn: {t.owner_action}</span>
              <button
                type="button"
                className="btn btn-sm btn-second"
                disabled={patch.isPending}
                onClick={() => apply({ owner_action: "" }, "Marked done – the turn moves on.", { owner_action: t.owner_action ?? "" })}
              >
                Done
              </button>
            </div>
          </div>
        ) : null}
        {t.close_note ? <div className="text-xs text-ink-low">Close note: {t.close_note}</div> : null}
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            className={clsx("btn btn-sm", t.christoph_only ? "btn-main" : "btn-ghost")}
            aria-pressed={t.christoph_only}
            disabled={patch.isPending}
            onClick={() =>
              apply(
                { christoph_only: !t.christoph_only },
                t.christoph_only ? "The seat works this thread again." : "Christoph only – the seats stay out of this thread.",
                { christoph_only: t.christoph_only },
              )
            }
          >
            Christoph only
          </button>
          {t.status === "open" ? (
            <>
              <select
                className="input input-sm w-[150px]"
                aria-label="Close as"
                value={closeAs}
                onChange={(e) => setCloseAs(e.target.value as ClosedAs)}
              >
                {CLOSE_OPTIONS.map((c) => (
                  <option key={c} value={c}>
                    {CLOSED_LABEL[c]}
                  </option>
                ))}
              </select>
              <input
                className="input input-sm flex-1 min-w-[140px]"
                aria-label="Close note"
                placeholder="Note (optional)"
                value={closeNote}
                onChange={(e) => setCloseNote(e.target.value)}
              />
              <button
                type="button"
                className="btn btn-sm btn-ghost"
                disabled={patch.isPending}
                onClick={() =>
                  apply(
                    { close_as: closeAs, ...(closeNote.trim() ? { close_note: closeNote.trim() } : {}) },
                    `Thread closed as ${CLOSED_LABEL[closeAs]}.`,
                    { reopen: true },
                  )
                }
              >
                Close
              </button>
            </>
          ) : (
            <button
              type="button"
              className="btn btn-sm btn-ghost"
              disabled={patch.isPending}
              onClick={() =>
                apply({ reopen: true }, "Thread reopened.", t.closed_as ? { close_as: t.closed_as } : undefined)
              }
            >
              Reopen
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
